<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <CRow class="align-items-center">
          <CCol md="6">
            <h1 class="h4 mb-1 font-weight-bold">Expenses</h1>
            <p class="text-muted mb-0">Receipts and expenses you've recorded</p>
          </CCol>
          <CCol md="6" class="d-flex justify-content-md-end mt-3 mt-md-0">
            <CButton color="light" class="mr-2" aria-label="Refresh" title="Refresh" @click="refreshTable">
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Add expense
            </CButton>
          </CCol>
        </CRow>
      </CCardBody>
    </CCard>

    <CCard class="mb-4 border-0 shadow-sm">
      <CCardBody class="p-4">
        <!-- Loading -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="sr-only">Loading…</span>
          </div>
          <p class="mt-3 text-muted">Loading expenses…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
          <h5>Couldn't load expenses</h5>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty -->
        <div v-else-if="!items.length" class="text-center py-5">
          <CIcon name="cil-inbox" size="2xl" class="mb-3 text-muted" />
          <h5>No expenses yet</h5>
          <p class="text-muted mb-3">Expenses you record will show up here.</p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Add expense
          </CButton>
        </div>

        <template v-else>
          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="computedItems"
              :fields="fields"
              items-per-page-select
              :items-per-page="25"
              hover
              sorter
              pagination
              :loading="loading"
            >
              <template #totalAmount="{ item }">
                <td class="text-right">RM {{ formatMyr(item.totalAmount) }}</td>
              </template>
              <template #show_details="{ item }">
                <td class="text-right text-nowrap">
                  <CButton color="light" size="sm" class="mr-1" aria-label="Edit expense" title="Edit" @click="onEdit(item)">
                    <CIcon name="cil-pencil" />
                  </CButton>
                  <CButton color="light" size="sm" aria-label="Delete expense" title="Delete" @click="showDeleteConfirmation(item)">
                    <CIcon name="cil-trash" />
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile cards -->
          <div class="d-lg-none">
            <CCard v-for="item in computedItems" :key="item.id" class="mb-3 border">
              <CCardBody class="py-2">
                <div class="d-flex justify-content-between">
                  <strong>{{ item.name || "Expense" }}</strong>
                  <strong class="text-right">RM {{ formatMyr(item.totalAmount) }}</strong>
                </div>
                <div class="small text-muted">{{ item.date }}</div>
              </CCardBody>
              <CCardFooter class="py-2 d-flex justify-content-between bg-white">
                <CButton color="light" size="sm" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CButton color="light" size="sm" class="text-danger" @click="showDeleteConfirmation(item)">
                  <CIcon name="cil-trash" /> Delete
                </CButton>
              </CCardFooter>
            </CCard>
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete Confirmation -->
    <CModal
      :title="'Delete ' + (itemToDelete.name || 'this expense') + '?'"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
    >
      <p class="mb-0">
        The RM {{ formatMyr(itemToDelete.totalAmount) }} expense will be removed. This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">Keep it</CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          {{ deleting ? "Deleting…" : "Delete expense" }}
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import ExpenseApi from "@/lib/expenseApi";
import { formatMyr } from "@/utils/money";
import { formatDate, formatDateTime } from "@/utils/datetime";

const fields = [
  { key: "createdOn", label: "Created" },
  { key: "date", label: "Date" },
  { key: "name", label: "Name" },
  { key: "totalAmount", label: "Amount", _style: "text-align: right", _classes: "text-right" },
  {
    key: "show_details",
    label: "",
    _style: "width:1%",
    sorter: false,
    filter: false,
  },
];

export default {
  name: "ExpenseList",
  data() {
    return {
      loading: false,
      loadError: false,
      deleting: false,
      items: [],
      fields,
      api: new ExpenseApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },
  mounted() {
    this.refreshTable();
  },
  computed: {
    computedItems() {
      return this.items.map((item) => ({
        ...item,
        createdOn: formatDateTime(item.createdOn),
        date: formatDate(item.date),
      }));
    },
  },
  methods: {
    formatMyr,
    refreshTable() {
      this.loading = true;
      this.loadError = false;
      this.api
        .getList()
        .then((response) => {
          this.items = response.result || [];
        })
        .catch((err) => {
          console.error("Failed to load expenses:", err);
          this.loadError = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/Bill/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.$toast.success("Expense deleted");
          this.warningModal = false;
          this.itemToDelete = {};
          this.refreshTable();
        })
        .catch((err) => {
          this.$toast.error(
            "Couldn't delete the expense. " + helper.getErrorMessage(err && err.data, "Please try again.")
          );
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    addNew() {
      this.$router.push({ path: "/tenants/Bill" });
    },
  },
};
</script>
